import React, { useEffect, useReducer, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Select,
  TextField,
} from '@material-ui/core';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { formDataValidate } from '../../../../../utils/formUtils';
import {
  vendorAttributeList,
  LIGHT,
  SCENE,
  CURTAIN,
  THERMO,
  DDK,
  DOOR_LOCK,
  DOOR_CAMERA,
  DND,
  MMR,
  VENDOR_CREATE_LIGHT,
  VENDOR_CREATE_SCENE,
  VENDOR_CREATE_CURTAIN,
  VENDOR_CREATE_THERMO,
  VENDOR_CREATE_DDK,
  VENDOR_CREATE_DOOR_LOCK,
  VENDOR_CREATE_DOOR_CAMERA,
  VENDOR_CREATE_DND,
  VENDOR_CREATE_MMR,
  VENDOR_EDIT_LIGHT,
  VENDOR_EDIT_SCENE,
  VENDOR_EDIT_CURTAIN,
  VENDOR_EDIT_THERMO,
  VENDOR_EDIT_DDK,
  VENDOR_EDIT_DOOR_LOCK,
  VENDOR_EDIT_DOOR_CAMERA,
  VENDOR_EDIT_DND,
  VENDOR_EDIT_MMR,
} from '../../../../../constants/VendorConst';
import { createNewVendor, updateExistingVendor } from '../../../../../utils/IntegrationsAPIUtils';
import WarningPopup from '../../../../../shared/warning-popup/Warning-Popup';
import { RemoveCircle } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import CancelIcon from '@material-ui/icons/Cancel';
import './VendorCreate.scss';
import { useDispatch, useSelector } from 'react-redux';
import { greTemplatesSelector } from '../../../configurationSlice';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const initialState = {
  name: '',
  status: false,
};

const VendorCreate = (props) => {
  const dispatch = useDispatch();
  const originalTemplates = useSelector(greTemplatesSelector);
  const { type, toggleDrawer, data, refreshVendors, hotelId, showLoader = () => {} } = props;
  const [attributes, setAttributes] = useState([]);
  const [isDisabledSubmit, setIsDisableSubmit] = useState(true);
  const [attributesDisabled, setAttributesDisabled] = useState([]);
  const [allAttributes, setAllAttributes] = useState(vendorAttributeList);
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Vendor Name',
      errors: {},
      validations: ['required'],
    },
  });
  const [warningPopup, setWarningPopup] = useState(initialState);
  const normalize = (search = '') =>
    search
      ?.toString()
      ?.toLowerCase()
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '');
  const [searchText, setSearchText] = useState('');
  const filteredAttributes = React?.useMemo(() => {
    if (!searchText?.trim()) return vendorAttributeList;
    const term = normalize(searchText);
    return vendorAttributeList?.filter(
      ({ name, value }) => normalize(name)?.includes(term) || normalize(value)?.includes(term),
    );
  }, [searchText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedType = handleCreateOrEditType();

    selectedType && selectedType?.action === 'create'
      ? handleCreateNewVendor(selectedType?.type)
      : handleUpdateExisitingVendor(selectedType?.type);
  };

  const handleCreateNewVendor = (selectedType) => {
    try {
      createNewVendor(hotelId, formData?.name?.value, selectedType, attributes).then((res) => {
        notificationManager('success', 'Vendor created successfully');
        refreshVendors();
        onCancel();
      });
    } catch (error) {
      if (error?.message) {
        notificationManager('danger', error?.message);
      } else {
        notificationManager('danger', 'Failed to create Vendor');
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleUpdateExisitingVendor = (selectedType) => {
    try {
      dispatch(stepUpLoaderCount());
      updateExistingVendor(data?.id, hotelId, formData?.name?.value, selectedType, attributes).then(
        (res) => {
          notificationManager('success', 'Vendor updated successfully');
          refreshVendors();
          onCancel();
        },
      );
    } catch (error) {
      if (error?.message) {
        notificationManager('danger', error?.message);
      } else {
        notificationManager('danger', 'Failed to update Vendor.');
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onCancel = () => {
    toggleDrawer(false);
  };

  const onCloseWarning = () => {
    setWarningPopup(initialState);
  };

  const onDeleteAttribute = (value) => {
    const attributeCopy = attributes?.filter((item) => item !== value);
    setAttributes(attributeCopy);
    setAttributesDisabled((prevState) => prevState?.filter((item) => item !== value));
  };

  const handleAttribute = (value) => {
    let attributeArrCopy = [...attributes];
    if (attributeArrCopy?.includes(value)) {
      attributeArrCopy = attributeArrCopy?.filter((item) => item !== value);
    } else {
      attributeArrCopy?.push(value);
    }
    setAttributes(attributeArrCopy);
  };

  const handleSearch = (e) => {
    const userValue = e.target.value;
    const allAttributesCopy = [...allAttributes];
    const temporaryList = allAttributesCopy?.filter((data) =>
      data?.name?.toLowerCase()?.includes(userValue?.toLowerCase()),
    );
    setAllAttributes(userValue !== '' ? [...temporaryList] : vendorAttributeList);
  };

  const handleCreateOrEditType = () => {
    switch (type) {
      case VENDOR_CREATE_LIGHT:
        return { type: LIGHT, action: 'create' };
      case VENDOR_EDIT_LIGHT:
        return { type: LIGHT, action: 'edit' };
      case VENDOR_CREATE_SCENE:
        return { type: SCENE, action: 'create' };
      case VENDOR_EDIT_SCENE:
        return { type: SCENE, action: 'edit' };
      case VENDOR_CREATE_CURTAIN:
        return { type: CURTAIN, action: 'create' };
      case VENDOR_EDIT_CURTAIN:
        return { type: CURTAIN, action: 'edit' };
      case VENDOR_CREATE_THERMO:
        return { type: THERMO, action: 'create' };
      case VENDOR_EDIT_THERMO:
        return { type: THERMO, action: 'edit' };
      case VENDOR_CREATE_DDK:
        return { type: DDK, action: 'create' };
      case VENDOR_EDIT_DDK:
        return { type: DDK, action: 'edit' };
      case VENDOR_CREATE_DOOR_LOCK:
        return { type: DOOR_LOCK, action: 'create' };
      case VENDOR_EDIT_DOOR_LOCK:
        return { type: DOOR_LOCK, action: 'edit' };
      case VENDOR_CREATE_DOOR_CAMERA:
        return { type: DOOR_CAMERA, action: 'create' };
      case VENDOR_EDIT_DOOR_CAMERA:
        return { type: DOOR_CAMERA, action: 'edit' };
      case VENDOR_CREATE_DND:
        return { type: DND, action: 'create' };
      case VENDOR_EDIT_DND:
        return { type: DND, action: 'edit' };
      case VENDOR_CREATE_MMR:
        return { type: MMR, action: 'create' };
      case VENDOR_EDIT_MMR:
        return { type: MMR, action: 'edit' };
      default:
        return null;
    }
  };

  const isVendorUsed = () => {
    switch (props.type) {
      case VENDOR_EDIT_LIGHT:
        return originalTemplates?.find((vendor) => vendor?.lightsVendor?.id === data?.id);
      case VENDOR_EDIT_SCENE:
        return originalTemplates?.find((vendor) => vendor?.scenesVendor?.id === data?.id);
      case VENDOR_EDIT_THERMO:
        return originalTemplates?.find((vendor) => vendor?.thermostatVendor?.id === data?.id);
      case VENDOR_EDIT_CURTAIN:
        return originalTemplates?.find((vendor) => vendor?.curtainsVendor?.id === data?.id);
      case VENDOR_EDIT_DOOR_LOCK:
        return originalTemplates?.find((vendor) => vendor?.doorLockVendor?.id === data?.id);
      default:
        break;
    }
  };

  useEffect(() => {
    if (data) {
      setAttributes(data?.enabledAttributes);
      setAttributesDisabled(data?.enabledAttributes);
    }
  }, [data]);

  useEffect(() => {
    if (data && data?.name) {
      const currFields = { ...formData };
      currFields.name.value = data?.name ?? '';
    }
  }, [data]);

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsDisableSubmit(!(isValid && attributes?.length !== 0));
  }, [attributes?.length, formData]);

  return (
    <>
      {warningPopup?.status && (
        <WarningPopup warningPopup={warningPopup} onWarningClose={onCloseWarning} />
      )}
      <form onSubmit={handleSubmit} className='vendor-create' noValidate>
        <div className='container pt-4 d-flex align-items-end flex-column'>
          <div className='row px-3 w-100 mt-4'>
            <div className='col-md-6'>
              <span className='page-title'>{data ? 'Edit Vendor' : 'Add Vendor'}</span>
            </div>
            <div className='col-md-6 d-flex justify-content-end'>
              <IconButton aria-label='delete' onClick={(e) => onCancel()}>
                <CancelIcon />
              </IconButton>
            </div>
          </div>
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.name}
                  handleInputChange={(e) => {
                    setFormData({ name: e });
                  }}
                  maxLength={15}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <FormControl variant='standard' className='w-100'>
                  <InputLabel>
                    Select Attributes <span className='vendor-create-required'>*</span>
                  </InputLabel>
                  <Select
                    id='new-vendor-attr'
                    multiple
                    required
                    value={[]}
                    onClose={() => setSearchText('')}
                    MenuProps={{
                      disableAutoFocusItem: true,
                    }}
                  >
                    <div
                      className='mx-4 mb-1'
                      onKeyDown={(e) => e?.stopPropagation()}
                      onClick={(e) => e?.stopPropagation()}
                    >
                      <TextField
                        autoFocus
                        label='Search'
                        value={searchText}
                        onChange={(e) => setSearchText(e?.target?.value)}
                        className='w-75'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              {searchText ? (
                                <IconButton
                                  size='small'
                                  aria-label='Clear search'
                                  onClick={() => setSearchText('')}
                                >
                                  <CancelIcon fontSize='small' />
                                </IconButton>
                              ) : (
                                <SearchIcon fontSize='small' />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    {filteredAttributes.length === 0 ? (
                      <div className='px-4 py-2 text-muted'>No Matching Attributes</div>
                    ) : (
                      filteredAttributes?.map((item) => {
                        const isDisabled = attributesDisabled?.includes(item?.value);
                        return (
                          <label
                            key={item?.id || item?.value}
                            value={item?.value}
                            className='d-flex column align-items-center mx-3'
                            style={{
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              userSelect: 'none',
                            }}
                            onMouseDown={(e) => e?.preventDefault()}
                          >
                            <Checkbox
                              checked={attributes?.includes(item?.value)}
                              color={'primary'}
                              onChange={() => handleAttribute(item?.value)}
                              disabled={isDisabled}
                              inputProps={{ 'aria-label': item?.name }}
                            />
                            <ListItemText primary={item?.name} />
                          </label>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className='row mt-2 w-100 pb-5'>
              <div className='d-flex flex-column p-0'>
                {attributes?.map((item, index) => (
                  <div
                    key={index}
                    className='d-flex flex-row justify-content-between align-items-center col-md-6 mb-3 p-3 vendor-create-attribute'
                  >
                    {item}
                    <RemoveCircle
                      className='pointer'
                      onClick={(e) =>
                        isVendorUsed()
                          ? setWarningPopup({ name: item, status: true })
                          : onDeleteAttribute(item)
                      }
                      color='dark'
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
            <div className='col-md-12 d-flex justify-content-start'>
              <Button
                variant='contained'
                type='submit'
                color='primary'
                className='me-3'
                size='large'
                disabled={isDisabledSubmit}
              >
                {data ? 'Update' : 'Add'}
              </Button>
              <Button
                variant='outlined'
                type='button'
                color='secondary'
                size='large'
                onClick={(e) => onCancel(e)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default VendorCreate;
